import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Aperture, KeyRound, Loader2, Download, Lock, CreditCard, ArrowLeft, LogOut, CheckCircle2 } from "lucide-react";
import { api, API } from "@/lib/api";

const inputCls = "w-full bg-[#0D0D0C] border border-[#F4F0EB]/10 rounded-lg px-4 py-3 font-sub text-sm text-[#F4F0EB] placeholder:text-[#6E695E] focus:border-[#C88D51]/60 focus:outline-none transition-colors";

export default function ClientGallery() {
  const [code, setCode] = useState("");
  const [input, setInput] = useState("");
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [paying, setPaying] = useState(false);

  const access = async (c) => {
    setLoading(true);
    try {
      const { data } = await api.post("/galleries/access", { code: c });
      setGallery(data);
      setCode(c);
      sessionStorage.setItem("sl_gallery_code", c);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code d'accès invalide");
      sessionStorage.removeItem("sl_gallery_code");
    }
    setLoading(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const saved = sessionStorage.getItem("sl_gallery_code");
    if (sid && saved) {
      setChecking(true);
      let tries = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/galleries/pay/status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("Paiement reçu ! Vos téléchargements sont débloqués ✦");
            window.history.replaceState({}, "", "/espace-client");
            setChecking(false);
            access(saved);
            return;
          }
        } catch (e) {}
        if (++tries < 8) setTimeout(poll, 2000);
        else { setChecking(false); access(saved); }
      };
      poll();
    } else if (saved) {
      access(saved);
    }
  }, []);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/galleries/pay", { gallery_id: gallery.id, code, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de paiement");
      setPaying(false);
    }
  };

  const logout = () => { sessionStorage.removeItem("sl_gallery_code"); setGallery(null); setCode(""); setInput(""); };

  const locked = gallery && gallery.balance_status !== "paid" && gallery.balance_due > 0;

  const download = (p) => {
    if (locked) { toast.error("Réglez le solde pour télécharger vos photos."); return; }
    window.open(`${API}/galleries/${gallery.id}/photo/${p.id}?code=${encodeURIComponent(code)}&download=1`, "_blank");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0D0D0C] flex items-center justify-center text-center px-6">
        <div><Loader2 className="w-12 h-12 text-[#C88D51] animate-spin mx-auto mb-5" /><p className="font-serif-d text-2xl">Vérification du paiement…</p></div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-[#0D0D0C] flex items-center justify-center px-6">
        <form onSubmit={(e) => { e.preventDefault(); if (input.trim()) access(input.trim()); }}
          className="w-full max-w-sm bg-[#161513] border border-[#F4F0EB]/8 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-2"><Aperture className="w-5 h-5 text-[#C88D51]" /><span className="font-serif-d text-lg">Sel &amp; Lumière</span></div>
          <h1 className="font-serif-d text-2xl mb-1">Espace client</h1>
          <p className="font-sub text-sm text-[#6E695E] mb-6 flex items-center gap-2"><KeyRound className="w-3 h-3" /> Entrez le code d'accès reçu avec vos photos</p>
          <input data-testid="client-gallery-code-input" placeholder="Code d'accès" value={input} onChange={(e) => setInput(e.target.value)} className={inputCls} />
          <button data-testid="client-gallery-code-submit" disabled={loading} className="btn-accent w-full mt-4 py-3.5 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Accéder à ma galerie
          </button>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 font-sub text-xs text-[#6E695E] hover:text-[#C88D51]"><ArrowLeft className="w-3 h-3" /> Retour au site</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0C]">
      <header className="border-b border-[#F4F0EB]/10 bg-[#141412]">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Aperture className="w-5 h-5 text-[#C88D51]" /><span className="font-serif-d">Sel &amp; Lumière</span></Link>
          <button data-testid="client-gallery-logout" onClick={logout} className="btn-ghost px-4 py-2 font-sub text-xs flex items-center gap-2"><LogOut className="w-3.5 h-3.5" /> Quitter</button>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-12">
        <div className="overline mb-3">Galerie privée</div>
        <h1 data-testid="client-gallery-title" className="font-serif-d text-3xl lg:text-5xl tracking-tight mb-2">{gallery.client_name}</h1>
        <p className="font-sub text-sm text-[#6E695E] mb-8">{gallery.photos.length} photo{gallery.photos.length > 1 ? "s" : ""} livrée{gallery.photos.length > 1 ? "s" : ""}</p>

        {locked ? (
          <div data-testid="client-gallery-balance-banner" className="bg-[#161513] border border-[#C88D51]/40 rounded-xl p-6 mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-[#C88D51] mt-1 shrink-0" />
              <div>
                <div className="font-serif-d text-xl">Solde restant : <span className="text-[#C88D51]">{gallery.balance_due},00 €</span></div>
                <p className="font-sub text-sm text-[#A39E93]">Réglez le solde pour débloquer le téléchargement en haute définition.</p>
              </div>
            </div>
            <button data-testid="client-gallery-pay-button" onClick={pay} disabled={paying} className="btn-accent px-7 py-3.5 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60 shrink-0">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Régler le solde
            </button>
          </div>
        ) : (
          <div data-testid="client-gallery-paid-banner" className="flex items-center gap-2 font-sub text-sm text-[#A39E93] mb-10">
            <CheckCircle2 className="w-4 h-4 text-[#C88D51]" /> Solde réglé — téléchargements débloqués
          </div>
        )}

        {gallery.photos.length === 0 && <p className="font-sub text-[#6E695E]">Vos photos seront bientôt disponibles ici.</p>}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.photos.map((p, i) => (
            <div key={p.id} data-testid={`client-gallery-photo-${i}`} className="group relative aspect-square overflow-hidden rounded-lg border border-[#F4F0EB]/8">
              <img src={`${API}/galleries/${gallery.id}/photo/${p.id}?code=${encodeURIComponent(code)}`} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-[#0D0D0C]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <button data-testid={`client-gallery-download-${i}`} onClick={() => download(p)}
                  className="btn-accent px-4 py-2.5 font-sub text-xs flex items-center gap-2">
                  {locked ? <Lock className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />} Télécharger
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
